import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import { useEditableLesson } from '@/data/hook/lesson/useEditableLesson';
import useLessonReplacement from '@/data/hook/lesson/useLessonReplacement';
import useLessonWorkspace from '@/data/hook/lesson/useLessonWorkspace';
import { useSingleCache } from '@/data/hook/query/v2/useCacheManager';
import { deleteBook, updateBook } from '@/data/services/bookServices';
import { booksQueryKeys, lessonsQueryKeys } from '@/data/services/querykeys';
import { isPulished } from '@/functions/handleCourseStatusIcon';
import Book, { bookType, MaterialType } from '@/models/Book';
import Lesson from '@/models/Lesson';
import { toLessonBookPatch } from '@/utils/book/lessonBookPatch';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { ROUTES } from '@/utils/routes';

type UseLessonMaterialParams = {
  lessonId: number;
  bookType: bookType;
  bookId: number;
};

const useLessonMaterial = ({
  lessonId,
  bookType,
  bookId,
}: UseLessonMaterialParams) => {
  const { data: book, isInitialLoading } = useQuery({
    ...booksQueryKeys.get(bookId),
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });
  const lessonCache = useSingleCache<Lesson>(
    lessonsQueryKeys.get(lessonId).queryKey,
  );
  const { runEditable } = useEditableLesson(lessonId);
  const { courseLineId, moduleId, slug = '', unitId } = useLessonWorkspace();
  const { syncLesson } = useLessonReplacement();
  const navigate = useNavigate();

  const { mutateAsync: deleteMaterial } = useMutation({
    mutationFn: () =>
      runEditable(async editableLessonId => {
        await deleteBook({ lessonId: editableLessonId, bookType, bookId });
        return editableLessonId;
      }),
    onSuccess: editableLessonId => {
      if (editableLessonId !== lessonId) return;
      lessonCache.merge(toLessonBookPatch(bookType, null));
    },
    onError: error => alert.error(getErrorMessage(error)),
  });

  const openEditor = (editableLessonId: number, editableBook: Book) =>
    navigate(
      ROUTES.ADMIN.COURSE_LINES.MATERIAL_CHAPTER({
        courseLineId,
        moduleId,
        slug,
        unitId,
        lessonId: editableLessonId,
        bookType,
        bookId: editableBook.id,
        chapterId: editableBook.chapters[0].id,
      }),
    );

  const { mutate: editMaterial, isPending: isEditing } = useMutation({
    mutationFn: async (currentBook: Book) => {
      if (!isPulished(currentBook.status))
        return { lessonId, book: currentBook };
      const versioned = await runEditable(
        async editableLessonId => ({
          lessonId: editableLessonId,
          book: await updateBook({
            lessonId: editableLessonId,
            bookType,
            bookId,
          }),
        }),
        { skipOnVersioned: true },
      );
      if (versioned.lessonId !== lessonId) await syncLesson(versioned.lessonId);
      return versioned;
    },
    onSuccess: ({ lessonId, book }) => openEditor(lessonId, book),
    onError: error => alert.error(getErrorMessage(error)),
  });

  return {
    book,
    isLoading: isInitialLoading,
    deleteMaterial,
    editMaterial:
      book?.materialType === MaterialType.TEXT && book.chapters.length
        ? () => editMaterial(book)
        : undefined,
    isEditing,
  };
};

export default useLessonMaterial;
